import {
  BooleanOperator,
  QueryBuilderRule,
  Operator,
  QueryBuilderRow,
  isQueryBuilderSubquery,
  QueryBuilderSubquery,
} from '@ardoq/api-types';
import { Extend } from '@ardoq/common-helpers';
import { SpecialFilterKeys } from './filterEnums';

/**
 * A subquery with no further nesting, eg.
 * ```ts
 * {
 *   condition: 'OR',
 *   rules: [{ field: 'type', operator: 'equal', value: 'Depends on' }]
 * }
 * ```
 */
export type FlatQueryBuilderSubquery<Rule extends QueryBuilderRule> = {
  condition: BooleanOperator;
  rules: Rule[];
};

/**
 * A QueryBuilderRule specifically for filtering on the type field.
 */
export type TypeEqualsQueryBuilderRule = Extend<
  QueryBuilderRule,
  {
    field: 'type';
    operator: Operator.EQUAL;
    /** Holds the component/reference type by name */
    value: string;
  }
>;

export const isTypeEqualityQueryBuilderRule = (
  rule: QueryBuilderRow
): rule is TypeEqualsQueryBuilderRule => {
  return (
    (rule as QueryBuilderRule).field === 'type' &&
    (rule as QueryBuilderRule).operator === 'equal'
  );
};

export const isHasReferenceToFilter = (
  filterAttributeName: string
): filterAttributeName is SpecialFilterKeys.COMPONENT_HAS_REFERENCE_TO =>
  filterAttributeName === SpecialFilterKeys.COMPONENT_HAS_REFERENCE_TO;

export const isHasReferenceFromFilter = (
  filterAttributeName: string
): filterAttributeName is SpecialFilterKeys.COMPONENT_HAS_REFERENCE_FROM =>
  filterAttributeName === SpecialFilterKeys.COMPONENT_HAS_REFERENCE_FROM;

export type QuickPerspectiveTypeEqualitySubquery = Extend<
  QueryBuilderSubquery,
  { rules: TypeEqualsQueryBuilderRule[] }
>;
export const isPureTypeEqualitySubquery = (
  ruleRoot: QueryBuilderRow
): ruleRoot is QuickPerspectiveTypeEqualitySubquery =>
  Boolean(
    isQueryBuilderSubquery(ruleRoot) &&
    ruleRoot.condition === BooleanOperator.OR &&
    ruleRoot.rules.every(isTypeEqualityQueryBuilderRule)
  );
